// Active orders (SM-829): the orders of the node's users that are still
// working, in the selected currency (matched on the stake leg, so GREEN
// has none). Scope opens on the whole subtree — every permitted
// descendant's accounts too (SM-862; a subtree over 500 nodes answers 422
// BROKER_000111 — switch to Serviced directly or narrow to a descendant);
// ?scope=direct = accounts this node services. The User picker narrows the
// list to one account by reading that account's own route. Refreshes every
// 30 s. Read-only: nothing here cancels.
//
// orders.index.tsx, not orders.tsx: an orders.tsx route would be the
// parent of orders.history.tsx, and /orders/history would render this page.
//
//   GET /api/broker/{id}/orders?scope=&currency=&offset=&limit=
//   GET /api/broker/{id}/accounts/{aid}/orders?currency=&offset=&limit=   (a picked user: the account in the PATH)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { EmptyPage, Pager } from "@/components/ui/Common";
import { QueryBlock } from "@/components/ui/States";
import { OrdersTable } from "@/components/OrdersTable";
import { TRADING_PAGE_LIMIT, TradingFilters, validateTradingSearch, type TradingSearch } from "@/components/TradingFilters";
import { fetchAccountOrders, fetchOrders, keys } from "@/lib/queries";
import { ACTIVE_POLL_MS, scopeFromSearch } from "@/lib/trading";
import type { AccountScope, OrderListResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/orders/")({
  validateSearch: (s: Record<string, unknown>): TradingSearch => validateTradingSearch(s),
  beforeLoad: capabilityGuard("VIEW"),
  component: ActiveOrdersPage,
});

function ActiveOrdersPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const scope: AccountScope = scopeFromSearch(search.scope);
  const account = search.account;
  const page = { currency, offset: search.offset ?? 0, limit: TRADING_PAGE_LIMIT };
  const nodeParams = { scope, ...page };
  const probe: { queryKey: QueryKey; queryFn: () => Promise<OrderListResponse> } = account
    ? { queryKey: keys.accountOrders(nodeId, account, page), queryFn: () => fetchAccountOrders(nodeId, account, page) }
    : { queryKey: keys.orders(nodeId, nodeParams), queryFn: () => fetchOrders(nodeId, nodeParams) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow", refetchInterval: ACTIVE_POLL_MS });
  const set = (patch: Partial<TradingSearch>) => void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });
  const onOffset = (o: number) => set({ offset: o || undefined });
  const hint = t(account ? "trading.emptyHintAccount" : "trading.emptyHintNode");

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("orders.title")}
        subtitle={t("orders.subtitle")}
        coverage={scope === "subtree" ? "subtree" : "node"}
        money
      />
      <SectionCard title={t("orders.activeList")}>
        <TradingFilters scope={scope} account={account} onScope={(v) => set({ scope: v })} onAccount={(a) => set({ account: a })} />
        <QueryBlock
          query={q}
          empty={(d) =>
            (d.orders ?? []).length === 0 ? <EmptyPage title={t("orders.emptyActive")} hint={hint} page={d.page} onOffset={onOffset} /> : null
          }
        >
          {(d) => (
            <>
              <OrdersTable orders={d.orders ?? []} variant="active" />
              <Pager page={d.page} onOffset={onOffset} />
            </>
          )}
        </QueryBlock>
      </SectionCard>
    </Guarded>
  );
}
